import { Component } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import moment from 'moment';
import { debounceTime } from 'rxjs';
import { generateUUID } from 'src/app/core/generators/generateUUID';
import { AuthService } from 'src/app/services/AuthService/auth.service';
import {
  ICreateMotoristaUpload,
  IGetMotorista
} from 'src/app/services/MotoristaService/motorista.model';
import { MotoristaService } from 'src/app/services/MotoristaService/motorista.service';
import { UploadService } from 'src/app/services/UploadService/upload.service';
import Swal from 'sweetalert2';

import { ITabelaMotorista } from './listagem-motorista.model';
import {
  formatarCpfCnpj,
  formatarTelefone
} from 'src/app/core/utils/utilities';
import { SwalAlertService } from 'src/app/services/SwalAlertService/swalAlert.service';

@Component({
  selector: 'app-listagem-motorista',
  templateUrl: './listagem-motorista.component.html',
  styleUrls: ['./listagem-motorista.component.scss']
})
export class ListagemMotoristaEmpresaComponent {
  isLoading: boolean = false;
  numeroPagina: number = 0;
  form = this._inicializarFormulario();
  tableData: ITabelaMotorista[] | Array<any> = [];
  pesquisaParams = {
    TxPesquisar: '',
    DtInicio: '',
    DtFim: '',
    Pagina: 0,
    TamanhoPagina: 20
  };
  maxPages: number = 1;
  expandirCardsMobile: boolean = true;
  guid = generateUUID();
  id: number = Number(localStorage.getItem('id'));

  tableColumns = [
    { header: 'Código', key: 'codigo', type: 'text' },
    { header: 'Motorista', key: 'motorista', type: 'text' },
    { header: 'CPF', key: 'cpf', type: 'text' },
    { header: 'CNH', key: 'cnh', type: 'text' },
    { header: 'Email', key: 'email', type: 'text' },
    { header: 'Telefone', key: 'telefone', type: 'text' },
    {
      header: 'Suspender',
      key: 'ativo',
      type: 'checkbox',
      action: (item: any) => this.suspenderMotorista(item)
    },
    {
      header: 'Ações',
      key: 'actions',
      type: 'buttons',
      buttons: [
        {
          label: 'Visualizar',
          img: 'assets/eye-icon.png',
          action: (item: any) => this.visualizarMotorista(item)
        },
        {
          label: 'Editar',
          img: 'assets/edit.svg',
          action: (item: any) => this.editarMotorista(item),
          condition: (item: any) => this.permitirEditar(item)
        },
        {
          label: 'Excluir',
          img: 'assets/trashcan.png',
          action: (item: any) => this.excluirMotorista(item)
        }
      ]
    }
  ];

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private motoristaService: MotoristaService,
    private authService: AuthService,
    private uploadService: UploadService,
    private swalAlert: SwalAlertService
  ) {}

  private _inicializarFormulario() {
    const form = this.fb.group({
      NM_USUA_MTRT: this.fb.control('', {
        nonNullable: true,
        validators: [Validators.required]
      }),
      TX_CPF_MTRT: this.fb.control('', {
        nonNullable: true,
        validators: [Validators.required]
      })
    });
    return form;
  }

  ngOnInit() {
    this.getMotorista();
  }

  ngAfterViewInit(): void {
    this.form.valueChanges.pipe(debounceTime(300)).subscribe(() => {
      this.getMotorista();
    });
  }

  navegarMotorista() {
    this.router.navigate(['empresa/cadastro/motorista']);
  }

  mudarPagina(pagina: number) {
    this.pesquisaParams.Pagina = pagina;
    this.numeroPagina = pagina;
    this.getMotorista(pagina + 1);
  }

  formatarCampos(campo: string | number | Date): string {
    return campo === null || campo === '' ? 'Não Informado' : String(campo);
  }

  getHours(date: Date) {
    if (date !== null) {
      return moment(date).format('HH:mm');
    }
    return '******';
  }

  getDate(date: Date) {
    if (date !== null) {
      return moment(date).format('DD/MM/YYYY');
    }
    return '******';
  }

  getMotorista(pageNumber?: number) {
    this.isLoading = true;

    const NM_USUA_MTRT = String(this.form.controls.NM_USUA_MTRT.value);
    const TX_CPF_MTRT = String(this.form.controls.TX_CPF_MTRT.value);

    this.authService.dadoLogin$.subscribe(({ idEmpresa }) => {
      this.motoristaService
        .GetMotoristaEmpresa({
          nomeMotoristaEmpresa: NM_USUA_MTRT,
          cpfMotoristaEmpresa: TX_CPF_MTRT,
          idEmpresa,
          pageNumber,
          pageSize: 20,
          status: 0
        })
        .subscribe({
          next: (response: IGetMotorista) => {
            this.isLoading = false;
            this.maxPages = response.TOTAL_PAGES;
            this.tableData = response.ITEMS.map(item => {
              return {
                codigo: item.PK_MTRT ?? 0,
                motorista: String(item.NM_USUA_MTRT),
                cpf: formatarCpfCnpj(String(item.TX_CPF_MTRT), 'cpf'),
                cnh: String(item.TX_CNH_MTRT),
                email: String(item.TX_MAIL_MTRT),
                telefone: formatarTelefone(String(item.NU_TELE_MTRT)),
                ativo: item.NU_STTS_MTRT == 1 ? false : true,
                situacao: item.NU_STTS_MTRT,
                empresa: item.FK_EPSA
              };
            });
          },
          error: (_error: any) => {
            this.isLoading = false;
          }
        });
    });
  }

  editarMotorista(item: any) {
    const queryParams = {
      ...item,
      modoMotorista: 'Editar'
    };
    this.router.navigate(['empresa/cadastro/motorista'], {
      queryParams: queryParams
    });
  }

  visualizarMotorista(item: any) {
    const queryParams = {
      ...item,
      modoMotorista: 'Visualizar'
    };
    this.router.navigate(['empresa/cadastro/motorista'], {
      queryParams: queryParams
    });
  }

  excluirMotorista(item: any) {
    this.swalAlert.showAlert(
      'Atenção!',
      'Deseja realizar a exclusão?',
      'warning',
      true,
      true,
      {
        cancelButtonText: 'Cancelar',
        cancelButtonColor: 'red',
        confirmButtonColor: 'green',
        confirmButtonText: 'Confirmar'
      },
      () => {
        this.isLoading = true;
        this.motoristaService.patchMotoristaEmpresa(item.codigo, 0).subscribe(
          response => {
            this.swalAlert.showAlert(
              'Sucesso!',
              'Excluído com sucesso.',
              'success',
              false,
              false,
              undefined
            );

            this.getMotorista(this.numeroPagina + 1);
          },
          error => {}
        );
        this.isLoading = false;
      }
    );
  }

  suspenderMotorista(item: any) {
    let statusMotorista: number;

    statusMotorista = item.situacao == 1 ? 2 : 1;

    this.motoristaService
      .patchMotoristaEmpresa(item.codigo, statusMotorista)
      .subscribe(
        response => {
          this.swalAlert.showAlert(
            'Sucesso!',
            `${
              statusMotorista == 1 ? 'Remoção da suspensão' : 'Suspensão'
            } realizada com sucesso.`,
            'success',
            true,
            false,
            {
              confirmButtonColor: 'green',
              confirmButtonText: 'Ok'
            },
            () => {
              this.getMotorista(this.numeroPagina + 1);
            }
          );
        },
        error => {}
      );
  }

  uploadArquivo(field: any) {
    this.isLoading = true;
    this.guid = generateUUID();
    const base64 = field.split(',')[1];

    const body: ICreateMotoristaUpload = {
      ReportBase64: base64,
      FK_EPSA: this.id
    };

    this.uploadService.createByUpload(body, this.guid, 'motorista').add(() => {
      this.isLoading = false;
      this.getMotorista(this.numeroPagina + 1);
    });
  }

  downloadExcel(): void {
    this.uploadService.downloadTemplateUpload('motorista');
  }

  permitirEditar(item: any): boolean {
    const status: boolean = item.situacao == 1 ? true : false;
    return status;
  }
}

